<template>
  <div class="group relative f-col">
    <base-button
      :aria-describedby="product.id"
      :aria-label="$t.add"
      class="absolute right-0 top-0 z-2 m-2 group-@hover:op-100 duration group-focus-within:op-100 lg:op-0"
      @click="dialogQuickshop.open({ product, variant: 'detailed' })"
    >
      <span class="block rounded-xs bg-white bg-op-80 duration">
        <vf-icon name="quickshop" size="md" />
      </span>
    </base-button>
    <base-picture
      :alt="product.gallery![0].alt || `${product.name} - 1`"
      class="w-full"
      :height="mediaHeight"
      :src="
        getResponsiveImageTransformations(product.gallery![0].src, {
          width: mediaWidth,
          height: mediaHeight,
          assetTransformation: product.gallery![0].assetTransformation,
          ...mediaTransformations,
        }) || defaultImageUrl
      "
      :width="mediaWidth"
    />
    <div class="f-col gap-1 p-3 md:p-2">
      <div v-if="showEyebrow" class="line-clamp-1 h-4 eyebrow c-brand-1">
        {{ formatBadgeOrEyebrow(product.eyebrow)?.label }}
      </div>
      <base-link :id="product.id" class="line-clamp-2 self-start subtitle-6" :to="product.url">
        {{ product.name }}
      </base-link>
      <span v-if="colorsCount" class="flex body-5 c-brand-1">
        {{ colorsCount > 15 ? '15+' : colorsCount }} {{ $pluralize($t.colors, colorsCount).toLowerCase() }}
      </span>
      <product-pricing class="body-5" :currency="product.currency" :price="productPrice" />
    </div>
    <dialog-quickshop :product-id="product.id" />
  </div>
</template>

<script lang="ts" setup>
import type { Product } from '#root/api/clients/product/data-contracts'

const { product } = defineProps<{ product: Product }>()

const { $feature } = useNuxtApp()
const route = useRoute()
const { card: { mediaSize, mediaTransformations }, defaultImageUrl } = useAppConfig().components.product
const dialogQuickshop = createDialog('quickshop')
const mediaWidth = normalizeResponsive(mediaSize.width)
const mediaHeight = normalizeResponsive(mediaSize.height)

const showEyebrow = computed(() => {
  const { mode, routes } = $feature.eyebrowVisibilityOnRoute
  const match = routes.some((pattern) => convertPatternToRegex(pattern).test(route.path))
  return mode === 'show' ? match : !match
})

const colorsCount = computed(() => {
  const colorAttr = product.attributes.find(({ type }) => type === 'color')?.options
  return colorAttr?.filter(({ available }) => available).length ?? 0
})

const productPrice = computed(() => {
  if (product.displayPriceRange) return product.price

  const swatches = product.attributes.find(({ type }) => type === 'color')?.options
  const colorSwatch = swatches?.find(({ id }) => id === product.id)
  return colorSwatch?.price || product.price
})
</script>
